import { TFunction } from 'i18next';
import React from 'react';
import { withTranslation } from 'react-i18next';
import Page from '../../components/page';
import { FormattedToken, LocalePrefix } from '../../types/common';
import { ConsentType, RawFormItem } from '../../types/config';
import ViewAnchor from '../../views/view-anchor';
import MobileOnly from '../../views/view-mobile-only';
import ClaimWalletConnect from '../../views/view-wallet-connect';
import AgeError from '../age-gate/error';
import AgeLanding from '../age-gate/landing';
import ClaimCapture from './capture';
import ClaimExpired from './expired';
import ClaimLanding from './landing';
import ClaimLink from './link';
import { ClaimPage, StateModel } from './model';
import ClaimOTP from './otp';
import Presenter from './presenter';
import ProveFlow from '../prove/flow';

interface ViewProps extends StateModel {
  t: TFunction;
  presenter: Presenter;
}

function ClaimMulti(props: ViewProps): JSX.Element {
  const {
    t,
    index,
    config,
    presenter,
    notificationType,
    authProvider,
    inputToken,
    formattedToken,
    avatarURL,
    inputName,
    socialName,
    inputCustom,
    inputAdditional,
    acceptedPrimaryConsent,
    acceptedMarketingConsent,
    isConsentPopupContinueEnabled,
    isAuthComplete,
    dateOfBirth,
    language,
    languagePolicies,
    showMobileOnly,
    continuationSessionId,
    continuationDraftId,
    showProveFallback,
    formItems,
    preFormItems,
  } = props;

  return (
    <>
      {showMobileOnly && (
        <MobileOnly
          onContinue={
            config.view_mode === 'mobile-first'
              ? (): void => presenter.onMobileFirstContinueClick()
              : undefined
          }
        />
      )}
      {!showMobileOnly && (
        <ViewAnchor prefix={LocalePrefix.claim} t={t}>
          <Page key="prove-flow" number={ClaimPage.prove} current={index}>
            <ProveFlow
              page="claim"
              config={config}
              showProveFallback
              language={language}
              languagePolicies={languagePolicies!}
              continuationSessionId={continuationSessionId}
              continuationDraftId={continuationDraftId}
              continuationPhoneNumber={inputToken}
              continuationDob={dateOfBirth ?? undefined}
              continuationFormItems={preFormItems}
              onBack={(): void => presenter.onBack('prove')}
            />
          </Page>
          <Page key="age-gate-landing" number={ClaimPage.ageGateLanding} current={index}>
            <AgeLanding
              minAge={config.age.min}
              format={config.age.format}
              onComplete={(date: any): void => {
                presenter.onAgeGatePassed(date);
              }}
              onError={(): void => {
                presenter.onAgeGateFailed();
              }}
            />
          </Page>
          <Page key="age-gate-error" number={ClaimPage.ageGateError} current={index}>
            <AgeError
              redirect={config.age.redirect}
              redirectDelay={config.age.delay ?? 5000}
              minAge={config.age.min}
            />
          </Page>
          <Page key="claim-expired" number={ClaimPage.expired} current={index}>
            <ClaimExpired config={config} />
          </Page>
          <Page key="claim-link" number={ClaimPage.link} current={index}>
            {formattedToken && (
              <ClaimLink
                config={config}
                notificationType={notificationType}
                formattedToken={formattedToken}
              />
            )}
          </Page>
          <Page key="claim-otp" number={ClaimPage.otp} current={index}>
            {formattedToken && (
              <ClaimOTP
                formattedToken={formattedToken}
                onBack={(): void => {
                  presenter.onBack();
                }}
              />
            )}
          </Page>
          <Page key="claim-landing" number={ClaimPage.landing} current={index}>
            <ClaimLanding
              language={language}
              config={config}
              showProveFallback={showProveFallback}
              acceptedPrimaryConsent={acceptedPrimaryConsent}
              acceptedMarketingConsent={acceptedMarketingConsent}
              isConsentPopupContinueEnabled={isConsentPopupContinueEnabled}
              onNext={(provider): void => {
                presenter.onNext(provider, ClaimPage.landing);
              }}
              onConsentChange={(type: ConsentType, consent: boolean): void =>
                presenter.onConsentChange(type, consent)
              }
              onPreFormItemsChange={(items: [RawFormItem]): void => {
                presenter.onPreFormItemsChange(items);
              }}
            />
          </Page>
          <Page key="claim-capture" number={ClaimPage.capture} current={index}>
            {authProvider && (
              <ClaimCapture
                config={config}
                authProvider={authProvider}
                onBack={(): void => {
                  presenter.onBack();
                }}
                onRegister={(nType: string, fToken?: FormattedToken): void => {
                  presenter.onRegister(authProvider, nType, fToken);
                }}
                inputToken={inputToken}
                onTokenChange={(value): void => presenter.onTokenInputChange(value)}
                avatarURL={avatarURL}
                socialName={socialName}
                inputName={inputName}
                onNameChange={(value): void => presenter.onNameInputChange(value)}
                inputCustom={inputCustom}
                onCustomChange={(value, custom): void =>
                  presenter.onCustomInputChange(value, custom)
                }
                inputAdditional={inputAdditional}
                onAdditionalChange={(value): void => presenter.onAdditionalChange(value)}
                acceptedPrimaryConsent={acceptedPrimaryConsent}
                acceptedMarketingConsent={acceptedMarketingConsent}
                onConsentChange={(type: ConsentType, consent: boolean): void =>
                  presenter.onConsentChange(type, consent)
                }
                isAuthComplete={isAuthComplete}
                language={language}
                dateOfBirth={dateOfBirth}
                formItems={formItems}
                preFormItems={preFormItems}
                onFormItemChange={(item, value): void => presenter.onFormItemChange(item, value)}
              />
            )}
          </Page>
          <Page key="claim-walletConnect-view" number={ClaimPage.signChallenge} current={index}>
            <ClaimWalletConnect
              onNext={(): void => {
                presenter.onNext('walletConnect', ClaimPage.signChallenge);
              }}
              onBackClick={(): void => {
                presenter.onBack();
              }}
              localePrefix={LocalePrefix.claim}
              language={language ?? undefined}
            />
          </Page>
        </ViewAnchor>
      )}
    </>
  );
}

export default withTranslation()(ClaimMulti);

ClaimMulti.Page = ClaimPage;
